const LOCAL_STORAGE_KEY = 'current_samples';

class SelectedSamples extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.currentSamplesWrapper = this.querySelector('[data-current-samples-wrapper]');
    this.sampleTemplate = this.querySelector('template[data-sample-template]');
    this.samplePlaceholderTemplate = this.querySelector(
      'template[data-sample-placeholder-template]'
    );
    this.hasAdditionalFormMessage = this.getAttribute('data-has-additional-form-message');
    this.additionalFormMessageTemplate = this.querySelector(
      'template[data-additional-form-message]'
    );
    this.dialogId = this.getAttribute('data-dialog-id');
    this.formId = this.getAttribute('data-form-id');
    // Will be filled later, as the app only hydrates after initial render.
    this.selectedSamplesInput = null;
    this.mutationObserver = null;

    this.purchaseButton = this.querySelector('[data-purchase-button]');
    this.purchaseButton.addEventListener('click', this.handlePurchaseButtonClick.bind(this));

    this.initializeSelectedSamples();
    this.initializeAdditionalFormMessage();

    // Event listeners
    this.unsubscribers = [
      subscribe('sample:add', ({ samples }) => this.handleSampleAddedEvent(samples)),
      subscribe('sample:remove', ({ samples }) => this.handleSampleRemovedEvent(samples)),
    ];
  }

  disconnectedCallback() {
    this.unsubscribers.forEach((unsubscriber) => unsubscriber());
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    if (this.#abortController) {
      this.#abortController.abort();
    }
  }

  /**
   * Initialize the selected samples.
   */
  initializeSelectedSamples() {
    const { success, samples } = this.getCurrentSamples();
    if (!success || !this.sampleTemplate) return;

    if (samples.length >= 1) {
      this.purchaseButton.removeAttribute('disabled');
    }

    samples.forEach((sample, index) => {
      const elementToReplace = this.currentSamplesWrapper.children[index];
      this.replaceWithFilledSample(sample, elementToReplace);
    });

    this.setupFormInputInitialization();
  }

  /**
   * Get the current samples from localStorage.
   * @returns {Object} - An object with a success flag and the samples array.
   */
  getCurrentSamples() {
    try {
      return {
        success: true,
        samples: JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [],
      };
    } catch (error) {
      console.error('Error retrieving samples', error);
      return { success: false, samples: [] };
    }
  }

  /**
   * Add a sample to the selected samples
   */
  replaceWithFilledSample(sample, elementToReplace) {
    const newSampleElement = this.sampleTemplate.content.cloneNode(true);
    const button = newSampleElement.querySelector('button');
    button.dataset.sampleId = sample.id;
    button.ariaLabel = button.getAttribute('aria-label').replace('{{ title }}', sample.title);
    button.addEventListener('click', this.handleSampleClick.bind(this));

    const image = newSampleElement.querySelector('img');
    image.src = sample.imageSrc;
    image.srcset = sample.imageSrcSet;
    image.alt = sample.title;

    this.currentSamplesWrapper.replaceChild(newSampleElement, elementToReplace);
  }

  /**
   * Handle when a sample is added
   */
  handleSampleAddedEvent(samples) {
    const indexOfNewSample = samples.length - 1;
    const newSample = samples[indexOfNewSample];
    this.replaceWithFilledSample(newSample, this.currentSamplesWrapper.children[indexOfNewSample]);
    this.purchaseButton.removeAttribute('disabled');
    this.fillFormInput(samples);
  }

  /**
   * Handle when a sample is removed
   */
  handleSampleRemovedEvent(samples) {
    for (let i = 0; i < this.currentSamplesWrapper.children.length; i++) {
      const sample = samples[i];
      if (sample) {
        this.replaceWithFilledSample(sample, this.currentSamplesWrapper.children[i]);
      } else {
        const childToReplace = this.currentSamplesWrapper.children[i];
        const placeholderSampleElement = this.samplePlaceholderTemplate.content.cloneNode(true);

        this.currentSamplesWrapper.replaceChild(placeholderSampleElement, childToReplace);
      }
    }
    this.fillFormInput(samples);
  }

  /**
   * Handle when a sample is clicked
   */
  handleSampleClick(event) {
    const button = event.currentTarget;
    const sampleId = button.dataset.sampleId;
    if (!sampleId) return;

    const { success, samples } = this.getCurrentSamples();
    if (!success) return false;

    const newSamples = samples.filter((sample) => sample.id !== sampleId);
    if (newSamples.length === 0) {
      this.purchaseButton.setAttribute('disabled', 'true');
    }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newSamples));
    publish('sample:remove', { id: sampleId, samples: newSamples });
  }

  handlePurchaseButtonClick() {
    window.location.href = `${window.location.href}#${this.dialogId}`;
  }

  setupFormInputInitialization() {
    const dialog = document.querySelector(`app-dialog[data-trigger="${this.dialogId}"]`);
    if (!dialog) return;
    const appForm = dialog.querySelector(`form[data-cf-form="${this.formId}"]`);
    if (!appForm) return;

    this.mutationObserver = new MutationObserver(() => {
      const input = appForm.querySelector('.sample-input-field input[type="text"]');
      if (input) {
        const { success, samples } = this.getCurrentSamples();
        if (!success) return;
        this.selectedSamplesInput = input;
        this.#abortController = new AbortController();
        this.preventAutofillOnInput(this.selectedSamplesInput);
        this.fillFormInput(samples);
        this.mutationObserver.disconnect();
      }
    });

    this.mutationObserver.observe(appForm, { subtree: true, childList: true });
  }

  /**
   * Discourage browser autofill on a programmatically controlled input.
   * @param {HTMLInputElement} input
   */
  preventAutofillOnInput(input) {
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('readonly', 'true');
  }

  /**
   * Set a React-controlled input value and notify the app of the change.
   * @param {HTMLInputElement} input
   * @param {string} value
   */
  setReactInputValue(input, value) {
    // The input is an input controlled by the helium customer fields app, which is react based. Setting the value doesn't correctly update the actual value of the input.
    const descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
    descriptor.set.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  fillFormInput(samples) {
    if (!this.selectedSamplesInput) return;
    const sampleString = samples.map((sample) => sample.id).join(',');
    this.setReactInputValue(this.selectedSamplesInput, sampleString);
  }

  /**
   * Initialize the additional form message.
   */
  initializeAdditionalFormMessage() {
    if (!this.hasAdditionalFormMessage) return;
    const dialog = document.querySelector(`app-dialog[data-trigger="${this.dialogId}"]`);
    if (!dialog) return;
    const dialogContent = dialog.querySelector('.app-blocks');
    if (!dialogContent) return;

    const messageFragment = this.additionalFormMessageTemplate.content.cloneNode(true);
    const messageElement = messageFragment.firstElementChild;
    dialogContent.appendChild(messageFragment);

    const appForm = dialog.querySelector(`form[data-cf-form="${this.formId}"]`);
    if (appForm && messageElement) {
      appForm.addEventListener('submit', () => {
        messageElement.remove();
      });
    }
  }
}
customElements.define('selected-samples', SelectedSamples);
